import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarDays, MapPin, User, Mail, Phone } from "lucide-react";

function toLocalInput(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function Booking() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const [v, setV] = useState(null);
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 86400000);
  const dayAfter = new Date(now.getTime() + 3 * 86400000);

  const [form, setForm] = useState({
    pickup_location: "Global Prime Fleet HQ — Plano, TX",
    dropoff_location: "Global Prime Fleet HQ — Plano, TX",
    pickup_datetime: toLocalInput(tomorrow),
    return_datetime: toLocalInput(dayAfter),
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/vehicles/${id}`).then(({ data }) => setV(data));
  }, [id]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, customer_name: user.name || "", customer_email: user.email || "" }));
  }, [user]);

  const days = Math.max(1, Math.ceil((new Date(form.return_datetime) - new Date(form.pickup_datetime)) / 86400000));
  const subtotal = v ? days * v.daily_price : 0;
  const tax = 0;
  const total = subtotal + tax;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        vehicle_id: id,
        pickup_location: form.pickup_location,
        dropoff_location: form.dropoff_location,
        pickup_datetime: new Date(form.pickup_datetime).toISOString(),
        return_datetime: new Date(form.return_datetime).toISOString(),
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        notes: form.notes,
      };
      const { data: booking } = await api.post("/bookings", payload);
      const { data: co } = await api.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = co.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Booking failed");
      setSubmitting(false);
    }
  };

  if (!v) return <div className="p-20">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="eyebrow mb-3" style={{ color: "#0B1F3A" }}>Reservation</div>
      <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter mb-10">Complete your booking</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-2xl border border-black/5 p-8 space-y-6">
          <Section title="Trip details">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input icon={<MapPin className="w-4 h-4 text-[#D4AF37]" />} label="Pick-up location" testId="booking-pickup"
                value={form.pickup_location} onChange={(v) => setForm({ ...form, pickup_location: v })} />
              <Input icon={<MapPin className="w-4 h-4 text-[#D4AF37]" />} label="Drop-off location" testId="booking-dropoff"
                value={form.dropoff_location} onChange={(v) => setForm({ ...form, dropoff_location: v })} />
              <Input icon={<CalendarDays className="w-4 h-4 text-[#D4AF37]" />} label="Pick-up date & time" testId="booking-pickup-dt" type="datetime-local"
                value={form.pickup_datetime} onChange={(v) => setForm({ ...form, pickup_datetime: v })} />
              <Input icon={<CalendarDays className="w-4 h-4 text-[#D4AF37]" />} label="Return date & time" testId="booking-return-dt" type="datetime-local"
                value={form.return_datetime} onChange={(v) => setForm({ ...form, return_datetime: v })} />
            </div>
          </Section>
          <Section title="Your details">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input icon={<User className="w-4 h-4 text-[#D4AF37]" />} label="Full name" testId="booking-name"
                value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} required />
              <Input icon={<Mail className="w-4 h-4 text-[#D4AF37]" />} label="Email" testId="booking-email" type="email"
                value={form.customer_email} onChange={(v) => setForm({ ...form, customer_email: v })} required />
              <Input icon={<Phone className="w-4 h-4 text-[#D4AF37]" />} label="Phone" testId="booking-phone"
                value={form.customer_phone} onChange={(v) => setForm({ ...form, customer_phone: v })} required />
            </div>
            <label className="block mt-4">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#0B1F3A]/60">Special requests (optional)</span>
              <textarea data-testid="booking-notes" rows="3"
                value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-2 w-full rounded-lg border border-[#0B1F3A]/15 focus:border-[#D4AF37] outline-none p-3 text-sm" />
            </label>
          </Section>
          <button data-testid="booking-continue-payment"
            className="btn-gold w-full justify-center" disabled={submitting}>
            {submitting ? "Redirecting to secure checkout…" : `Continue to secure payment — $${total.toFixed(2)}`}
          </button>
          <p className="text-xs text-[#0B1F3A]/50 text-center">You'll be redirected to Stripe to complete payment. Test card: 4242 4242 4242 4242.</p>
        </form>

        <aside className="bg-[#0B1F3A] text-white rounded-2xl p-8 h-fit sticky top-28">
          <img src={v.image_url} alt={v.name} className="w-full aspect-video object-cover rounded-lg mb-4" />
          <div className="eyebrow mb-2">{v.category}</div>
          <h3 className="font-display font-bold text-2xl mb-6">{v.name}</h3>
          <div className="space-y-3 text-sm text-white/80 border-t border-white/10 pt-6">
            <Row label="Daily rate" value={`$${v.daily_price.toFixed(2)}`} />
            <Row label="Duration" value={`${days} day${days > 1 ? "s" : ""}`} />
            <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
            <Row label="Taxes & fees" value={`$${tax.toFixed(2)}`} />
          </div>
          <div className="border-t border-white/10 mt-6 pt-6 flex items-baseline justify-between">
            <span className="text-sm uppercase tracking-widest text-white/60">Total</span>
            <span className="text-3xl font-display font-extrabold text-[#D4AF37]" data-testid="booking-total">${total.toFixed(2)}</span>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="eyebrow mb-4" style={{ color: "#0B1F3A" }}>{title}</div>
      {children}
    </div>
  );
}
function Input({ label, value, onChange, type = "text", icon, testId, required }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[10px] uppercase tracking-[0.2em] text-[#0B1F3A]/60">{label}</span>
      <div className="flex items-center gap-2 border border-[#0B1F3A]/15 rounded-lg px-3 py-2.5 focus-within:border-[#D4AF37] transition-colors">
        {icon}
        <input type={type} value={value} required={required}
          data-testid={testId}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-transparent outline-none text-sm text-[#0B1F3A]" />
      </div>
    </label>
  );
}
function Row({ label, value }) {
  return <div className="flex justify-between"><span>{label}</span><span className="font-medium">{value}</span></div>;
}
